import { useEffect, useMemo, useState, type UIEvent } from 'react';
import { SearchInput } from '@lmig/lmds-react-alphanumeric-input';
import { Popover } from '@lmig/lmds-react-popover';
import { DataTable, Table, TableCell, TableRow, TableHeader, TableBody } from '@lmig/lmds-react-table';
import { flexRender, Table as TanstackTable } from '@tanstack/react-table';
import './AgencyCodeSelector.scss';
import './BLdropdown.scss';
import { StringHelper } from 'Utilities/StringHelper';
import { useUserData } from 'Foundation/User/UserContext';
import { ColumnDef, SortingState, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';

export type BLAgencyRow = {
  agencyname: string;
  code: string;
  ecliq: string;
};

type AgencyNameApiItem = {
  AgencyCode?: string;
  AgencyName?: string;
};


export function BLDropdown() {

  const normalizeAgencyNumber = (value: string) => value.replaceAll('-', '').trim();
  const { userData } = useUserData();
  const [searchValue, setSearchValue] = useState<string>('');
  const [statCodes, setStatCodes] = useState<Agency[]>([]);
  const [agencyNamesByCode, setAgencyNamesByCode] = useState<Map<string, string>>(new Map());
  //fixes re-render on load to automatically get the right agency name
  //uses a useEffect through getAgencyNames 
  const [currentAgencyName, setCurrentAgencyName] = useState<string | undefined>();
  const [filteredCodes, setFilteredCodes] = useState<Agency[]>();
  const [tableFilterValue, setTableFilterValue] = useState<string>();
  const [sorting, setSorting] = useState<SortingState>([]);

  useEffect(() => {
    if (userData?.selectedStatCodeData.SubStat && agencyNamesByCode) {
      let name = agencyNamesByCode.get(normalizeAgencyNumber(userData?.selectedStatCodeData.SubStat ?? ''));
      if (name)
        setCurrentAgencyName(name);
    }
  }, [userData?.selectedStatCodeData.SubStat, agencyNamesByCode]);

  useEffect(() => {
    if (searchValue && statCodes) {
      const filterValue = searchValue.replaceAll('-', '');
      setFilteredCodes(statCodes.filter((code) => code.AgencyNumber.replaceAll('-', '').indexOf(filterValue) > -1));
    } else {
      setFilteredCodes(undefined);
    }
  }, [searchValue, statCodes]);


  const eCliqByAgencyNumber = useMemo(() => {
    const agencyCodes = userData?.agencyCodes ?? [];
    const eCliqCodes = userData?.eCliqCodes ?? [];
    const byCode = new Map<string, string>();

    agencyCodes.forEach((rawAgencyCode, index) => {
      byCode.set(StringHelper.formatStatCode(rawAgencyCode), eCliqCodes[index] ?? '');
    });

    return byCode;
  }, [userData?.agencyCodes, userData?.eCliqCodes]);
  const finalColumns = useMemo<ColumnDef<BLAgencyRow>[]>(
    () => [
      {
        header: 'Agency',
        accessorKey: 'agencyname',
        id: 'agencyname',
      },
      {
        header: 'Code',
        accessorKey: 'code',
        id: 'code',
      },
      {
        header: () => (
          <span className="ecliq-header-label">
            eCLIQ<sup className="ecliq-trademark">&reg;</sup>
          </span>
        ),
        accessorKey: 'ecliq',
        id: 'ecliq',
      },
    ],
    [],
  );

  const finalData = useMemo<BLAgencyRow[]>(
    () =>
      (statCodes ?? []).map((agency) => ({
        agencyname: agencyNamesByCode.get(normalizeAgencyNumber(agency.AgencyNumber)) ?? '-',
        code: agency.AgencyNumber,
        ecliq: eCliqByAgencyNumber.get(agency.AgencyNumber) ?? '',
      })),
    [statCodes, eCliqByAgencyNumber, agencyNamesByCode],
  );

  const filteredFinalData = useMemo<BLAgencyRow[]>(() => {
    const filterValue = tableFilterValue?.trim().toLowerCase();
    if (!filterValue) return finalData;

    return finalData.filter((row) => {
      return [row.agencyname, row.code, row.ecliq].some((value) => value.toLowerCase().includes(filterValue));
    });
  }, [finalData, tableFilterValue]);

  const blTable = useReactTable({
    data: filteredFinalData,
    columns: finalColumns,
    autoResetAll: false,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  const showFilter = () => statCodes.length >= 4;
  const getAgenciesFromUserData = (): Agency[] => {
    const codes = userData?.agencyCodes;
    if (!codes || codes.length === 0) return [];
    return codes.map((code) => ({
      AgencyNumber: StringHelper.formatStatCode(code),
      HasAccess: true,
    }));
  };

  useEffect(() => {
    async function getAgencyNames() {
      if (!statCodes.length) return;

      let agencyNameResults: AgencyNameApiItem[] = [];
      try {
        const response = await fetch(`/api/sitecore/kyberagencydetails/GetAgencyNames?ts=${Date.now()}`, {
          method: 'GET',
          cache: 'no-store',
        });
        if (!response.ok) {
          const errorBody = await response.text();
          throw new Error(
            `Agency names request failed (${response.status} ${response.statusText || 'Server Error'}): ${errorBody}`,
          );
        }
        const data = await response.json();
        agencyNameResults = Array.isArray(data) ? data : [];
      } catch (error: any) {
        console.error('Failed to load agency names from Kyber endpoint.', error?.message ?? error);
        agencyNameResults = [];
      }

      const nameByCode = new Map<string, string>();
      agencyNameResults.forEach((item) => {
        const agencyCode = item.AgencyCode || '';
        const agencyName = (item.AgencyName || '').trim();
        if (!agencyCode || !agencyName) return;
        nameByCode.set(normalizeAgencyNumber(agencyCode), agencyName);
      });

      setAgencyNamesByCode(nameByCode);
    }

    getAgencyNames();
  }, [statCodes]);

  useEffect(() => {
    if (statCodes.length === 0 && userData?.agencyCodes?.length)
      setStatCodes(getAgenciesFromUserData());
  }, [userData?.agencyCodes]);

  const agencyName = userData?.selectedStatCodeData && (
    <span className="agency-header-name">
      {userData?.selectedStatCodeData?.SubStat}
    </span>
  );
  const seeAgenciesLink = (
    <a className="see-agencies-link lm-Link lm-LinkStandalone cursor-pointer">
      See agencies
    </a>
  );



  return (
    <div className="top-nav-dropdown agency-header-frame">
      {agencyName}
      <Popover
        trigger={seeAgenciesLink}
        variant="custom"
        direction="below"
        align='end'
        className="agency-codes-popover"
      >
        <div className="popover-content bl-popover-content">
          <div className="agency-codes-main-content">
            <div className="agency-codes-top-section">
              <div className="agency-codes-header">
                <span className="agency-codes-title">Agency codes</span>
                <a
                  href="/agency-codes"
                  target="_blank"
                  rel="noreferrer"
                  className="agency-codes-expanded-link"
                  onClick={(e) => {
                    // Tablet/mobile navigates in the same tab per design.
                    if (window.matchMedia('(max-width: 960px)').matches) {
                      e.preventDefault();
                      window.location.assign('/agency-codes');
                    }
                  }}
                >
                  View all codes
                </a>
              </div>
              <span className="agency-codes-my-agency">{currentAgencyName ? `${currentAgencyName} (${StringHelper.formatStatCode(userData?.selectedStatCodeData?.SubStat ?? '')})` : userData?.selectedStatCodeData?.SubStat ? StringHelper.formatStatCode(userData.selectedStatCodeData.SubStat) : ''}</span>
              <div className="agency-codes-help-text">
                <p>Your login ID pulls information from the codes listed below.</p>
                <p>Can’t find a customer? They may belong to an agency code that isn’t on this list. Sign in with the ID tied to that agency, or ask your Territory Manager to add the code to this login.</p>
              </div>

              {showFilter() && (
                <div className="agency-codes-filter">
                  <SearchInput
                    id="agency-codes-filter"
                    labelVisual="Filter by code, agency, or eCLIQ®"
                    value={tableFilterValue}
                    placeholder="Filter by code, agency, or eCLIQ®"
                    onChange={(event) => setTableFilterValue(event.target.value)}
                    onClearClicked={() => setTableFilterValue('')}
                  />
                </div>
              )}
            </div>
            <div className="agency-codes-table-section">
              <div
                className={`agency-codes-table-wrapper${(filteredCodes ? filteredCodes.length : 0) > 9 ? ' scrollable' : statCodes.length > 9 ? ' scrollable' : ''}`}
              >
                <DataTable>
                  <Table className="agency-codes-table" id="agency-codes-table" spacing="compact">
                    <TableHeader>
                      {blTable.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                          {headerGroup.headers.map((header) => {
                            const sortState = header.column.getIsSorted();
                            const sortDirection = sortState === 'asc' ? 'ascending' : sortState === 'desc' ? 'descending' : 'none';
                            return (
                              <TableCell
                                key={header.id}
                                className={`agency-col-${header.column.id}`}
                                type="colHead"
                                isSortable={header.column.getCanSort()}
                                sortDirection={sortDirection}
                                onClick={header.column.getCanSort() ? (event) => {
                                  event.stopPropagation();
                                  header.column.getToggleSortingHandler()?.(event);
                                } : undefined}
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableHeader>
                    <TableBody>
                      {blTable.getRowModel().rows.map((row) => (
                        <TableRow key={row.id} className="agency-row">
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id} className={`agency-col-${cell.column.id}`}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </DataTable>
              </div>
              {tableFilterValue && filteredCodes?.length === 0 && <p className="no-matching-agency-codes">No matching agency codes</p>}
            </div>
          </div>
        </div>
      </Popover>
    </div>
  );
}
